import { Allocation } from '@assembly/pages/AssemblyTemplates/types'
import { Capability } from '@assembly/pages/Capabilities/types'
import { TeamMember } from '@assembly/pages/Teams/types'
import { Organization } from '@assembly/pages/OrganizationManager/types'

export type AssemblyLine = {
  id: string
  name: string
  description: string
  orgID: string
  org?: Organization | null
  capabilities: Capability[]
  monthlySubscriptionCost: number | null
  allocations: Allocation[]
  startDate: number
  endDate: number
  timezone: string
  owner: string | TeamMember | null
  members: TeamMember[]
  funding: Funding
  approved: boolean
  approvedBy: string
  status: string
  statusMessage: string
  projectManagement: ProjectManagement
  manager: ProjectManagement
  checklists: any
  requirements: {}
  sku: SKU
  restricted?: boolean
  dashboardSettings?: DashboardSettings
  assemblyLineSubscriptionConfigId?: string | null
}

export type TokenPricing = {
  inputTokensPerMillion?: number
  outputTokensPerMillion?: number
  voiceMinutesPerMinute?: number
  totalCredits?: number | null
  totalCreditsMonetaryValue?: number | null
  creditsStartTime?: number | null
  creditsEndTime?: number | null
}

export type FundingMetaData = {
  STRIPE_ACCOUNT_ID?: string
  STRIPE_CUSTOMER_ID?: string
  STRIPE_PAYMENT_METHOD_ID?: string
  STRIPE_SUBSCRIPTION_ID?: string
  STRIPE_INVOICE_ID?: string
  STRIPE_SETUP_PRODUCT_ID?: string
  STRIPE_SETUP_PRICE_ID?: string
  STRIPE_SETUP_ONE_TIME_PRICE_ID?: string
  STRIPE_LLM_INPUT_TOKENS_METER_ID?: string
  STRIPE_LLM_OUTPUT_TOKENS_METER_ID?: string
  STRIPE_CONVERSATION_VOICE_METER_ID?: string
  STRIPE_LLM_INPUT_TOKENS_PRICE_ID?: string
  STRIPE_LLM_OUTPUT_TOKENS_PRICE_ID?: string
  STRIPE_CONVERSATION_VOICE_PRICE_ID?: string
}

export type Funding = {
  billingAmount: number | null
  currency: string
  frequency: string
  startDate: string
  endDate: string
  lastSuccessfulPayment: number | null
  status: string
  statusMsg: string
  setupLink: string
  setupComplete?: boolean
  paymentLink: string
  apContacts: string[]
  projectType: string
  createContractorInvoices: boolean
  disableExternalInvoiceIntegration: boolean
  tokenPricing?: TokenPricing | null
  metaData?: FundingMetaData
}

export type FundingForm = {
  billingAmount: number | null
  currency: string
  frequency: string | null
  startDate: any
  endDate: any
  lastSuccessfulPayment: number | null
  status: string | null
  statusMsg: string
  setupLink: string
  paymentLink: string
  apContacts: string[]
  projectType: string
  createContractorInvoices: boolean
  disableExternalInvoiceIntegration: boolean
  tokenPricing: TokenPricing | null
  metaData: FundingMetaData
  invalid_billingAmount: boolean
  invalid_frequency: boolean
  invalid_startDate: boolean
  invalid_endDate: boolean
  invalid_status: boolean
  invalid_projectType: boolean
}

export type AssemblyLineForm = {
  name: string
  invalid_name: boolean
  description: string
  invalid_description: boolean
  organization?: Organization | null
  owner: TeamMember | null
  invalid_owner: boolean
  invalid_organization: boolean
  capabilities: Capability[]
  invalid_capabilities: boolean
  monthlySubscriptionCost: number | null
  invalid_monthlySubscriptionCost: boolean
  allocations: Allocation[]
  invalid_allocations: boolean
  startDate: any
  invalid_startDate: boolean
  invalid_endDate: boolean
  endDate: any
  timezone: string
  members: TeamMember[]
  invalid_members: boolean
  funding: FundingForm
  approved: boolean
  approvedBy: string
  status: string | null
  invalid_status: boolean
  statusMessage: string
  // Project Management
  enableProjectManagement: boolean
  projectManagementLink: string
  submitTaskLink: string
  kanbanLink: string
  teamID: string
  projectID: string
  requirements: {}
  sku: SKU
  invalid_sku: boolean
  restricted: boolean
  dashboardSettings: DashboardSettings
  assemblyLineSubscriptionConfigId?: string | null
  invalid_assemblyLineSubscriptionConfigId?: boolean
}

export enum FundingFrequency {
  OneTime = 'ONE_TIME',
  Monthly = 'MONTHLY',
  Quarterly = 'QUARTERLY',
  Yearly = 'YEARLY',
}

export enum FundingStatus {
  Initialized = 'INITIALIZED',
  SetupComplete = 'SETUP_COMPLETE',
  Active = 'ACTIVE',
  Paused = 'PAUSED',
  Error = 'ERROR',
}

export enum ProjectTypes {
  Recurring = 'RECURRING',
  OneTime = 'ONE_TIME',
  MonthlyInvoiceNet0 = 'MONTHLY_INVOICE_NET_0',
  MonthlyInvoiceNet7 = 'MONTHLY_INVOICE_NET_7',
  MonthlyInvoiceNet15 = 'MONTHLY_INVOICE_NET_15',
  MonthlyInvoiceNet30 = 'MONTHLY_INVOICE_NET_30',
  MonthlyInternal = 'MONTHLY_INTERNAL',
}

export const ProjectTypeDescriptions: Record<ProjectTypes, string> = {
  [ProjectTypes.Recurring]: "Subscription-based billing with automatic recurring charges handled by Stripe. No invoices are created by the system - Stripe manages all subscription billing automatically. Customer is charged automatically based on the subscription schedule. Stripe sends customer emails for subscription invoices. Billing amount is fixed and charged on a recurring schedule.",
  [ProjectTypes.OneTime]: "One-time payment invoiced only when the project is completed. Customer receives a single invoice via email when the project is marked complete. Payment is due within 30 days (NET_30 payment terms). Billing amount is fixed and set when the project is created.",
  [ProjectTypes.MonthlyInvoiceNet0]: "Monthly invoices are automatically created and sent on the 1st of each month for the previous month's work. Payment is charged immediately when the invoice is created (NET_0 - due on receipt). Customer receives invoice via email from Stripe. A final prorated invoice is sent when the project is completed. Billing amount is fixed monthly and charged automatically.",
  [ProjectTypes.MonthlyInvoiceNet7]: "Monthly invoices are automatically created and sent on the 1st of each month for the previous month's work. Payment is due within 7 days after the invoice is sent (NET_7 payment terms). Customer receives invoice via email from Stripe. A final prorated invoice is sent when the project is completed. Billing amount is fixed monthly.",
  [ProjectTypes.MonthlyInvoiceNet15]: "Monthly invoices are automatically created and sent on the 1st of each month for the previous month's work. Payment is due within 15 days after the invoice is sent (NET_15 payment terms). Customer receives invoice via email from Stripe. A final prorated invoice is sent when the project is completed. Billing amount is fixed monthly.",
  [ProjectTypes.MonthlyInvoiceNet30]: "Monthly invoices are automatically created and sent on the 1st of each month for the previous month's work. Payment is due within 30 days after the invoice is sent (NET_30 payment terms). Customer receives invoice via email from Stripe. A final prorated invoice is sent when the project is completed. Billing amount is fixed monthly.",
  [ProjectTypes.MonthlyInternal]: "Internal cost tracking only - no customer invoices or payment processing. Internal billing records are created on the 1st of each month for tracking purposes. Invoice amount is calculated from actual contractor costs based on work performed. No invoices are sent to customers and no payment is collected. Used for internal accounting and cost tracking.",
}

export enum AssemblyLineStatus {
  Initialized = 'INITIALIZED',
  Setup = 'SETUP',
  Proposal = 'PROPOSAL',
  Provisioning = 'PROVISIONING',
  Ready = 'READY',
  Active = 'ACTIVE',
  Paused = 'PAUSED',
  Complete = 'COMPLETE',
  Archived = 'ARCHIVED',
  Modify = 'MODIFY',
}

export enum SKU {
  ASSEMBLY_PARTNERS = 'ASSEMBLY_PARTNERS',
  ASSEMBLY_ORCHESTRATION = 'ASSEMBLY_ORCHESTRATION',
  ASSEMBLY_AGENT_SUBSCRIPTION = 'ASSEMBLY_AGENT_SUBSCRIPTION',
  FOUNDRY_APP = 'FOUNDRY_APP',
}

/** SKUs that use assemblyLineSubscriptionConfigId + usage credits billing. */
export function isUsageCreditsSku(sku: SKU | string | null | undefined): boolean {
  return sku === SKU.ASSEMBLY_AGENT_SUBSCRIPTION || sku === SKU.FOUNDRY_APP
}

/** SKUs where team members are optional at create time. */
export function isOptionalMembersSku(sku: SKU | string | null | undefined): boolean {
  return sku === SKU.ASSEMBLY_ORCHESTRATION || isUsageCreditsSku(sku)
}

/** SKUs where contractor resource allocations are optional. */
export function isOptionalAllocationsSku(sku: SKU | string | null | undefined): boolean {
  return sku === SKU.ASSEMBLY_ORCHESTRATION || isUsageCreditsSku(sku)
}

/** Human-readable label for SKU. */
export function getSkuDisplayLabel(sku: SKU | string | null | undefined): string {
  if (!sku) return '---'
  if (sku === SKU.ASSEMBLY_PARTNERS) return 'Assembly Partners'
  if (sku === SKU.ASSEMBLY_ORCHESTRATION) return 'Assembly Orchestration'
  if (sku === SKU.ASSEMBLY_AGENT_SUBSCRIPTION) return 'Assembly Agent Subscription'
  if (sku === SKU.FOUNDRY_APP) return 'Foundry App'
  return sku
}

export enum DashboardMode {
  SelfServiceAgents = 'SELF_SERVICE_AGENTS',
  AgenticAutomation = 'AGENTIC_AUTOMATION',
  SelfServiceAgentsAgenticAutomation = 'SELF_SERVICE_AGENTS_AGENTIC_AUTOMATION',
}

export type DashboardSettings = {
  mode: DashboardMode
}

export type PaginatedResponse<T> = {
  data: T[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
}

export type AssemblyLineQuery = {
  search?: string
  capabilityIds?: string[]
  teamIds?: string[]
  limit: number
  offset: number
  orgID?: string
  status?: AssemblyLineStatus
}

type ProjectManagement = {
  enableProjectManagement: boolean
  projectManagementLink: string
  submitTaskLink: string
  kanbanLink: string
  teamID: string
  projectID: string
}
